import * as Sentry from "@sentry/browser";
import { replayIntegration } from "@sentry/browser";

// Declare window.SentryToolbar type for CDN-loaded toolbar
declare global {
  interface Window {
    SentryToolbar?: {
      init: (config: { organizationSlug: string; projectIdOrSlug: string }) => () => void;
    };
  }
}

// Helper to get meta tag content
function getMetaContent(name: string): string | null {
  return document.querySelector(`meta[name="${name}"]`)?.getAttribute('content') || null;
}

const dsn = getMetaContent('sentry-dsn');
const environment = getMetaContent('sentry-environment');

// Only initialize if DSN is present (won't be in dev/test per backend config)
if (dsn) {
  // Determine if toolbar should be enabled
  // - Always on in development and staging
  // - In production/UAT only if user is admin
  const isAdmin = (getMetaContent('sentry-user-admin') === 'true');
  const shouldEnableToolbar =
    (environment === 'development') ||
    (environment === 'staging') ||
    (isAdmin && ['production', 'uat'].includes(environment || ''));

  Sentry.init({
    dsn,
    environment,
    integrations: [
      replayIntegration(),
    ],
    tracesSampleRate: 1.0,
  });

  // Set user context from meta tags
  const userId = getMetaContent('sentry-user-id');
  if (userId) {
    Sentry.setUser({
      id: userId,
      email: getMetaContent('sentry-user-email'),
      username: getMetaContent('sentry-user-name'),
      company_id: getMetaContent('sentry-user-company-id'), // eslint-disable-line @typescript-eslint/naming-convention
      company_name: getMetaContent('sentry-user-company-name'), // eslint-disable-line @typescript-eslint/naming-convention
    });
  } else {
    Sentry.setUser({ username: 'guest' });
  }

  // Set tags from meta
  const tenant = getMetaContent('sentry-tenant');
  if (tenant) {
    Sentry.setTag('tenant', tenant);
  }
  if (isAdmin) {
    Sentry.setTag('admin', true);
  }

  // Initialize Sentry Toolbar from CDN if it should be enabled
  if (shouldEnableToolbar && typeof window.SentryToolbar !== 'undefined') {
    const orgSlug = getMetaContent('sentry-org-slug');
    const projectSlug = getMetaContent('sentry-project-slug');

    if (orgSlug && projectSlug) {
      window.SentryToolbar.init({
        organizationSlug: orgSlug,
        projectIdOrSlug: projectSlug,
      });
    }
  }
}
